/**
 * InstallAppBanner — Batch #14.244
 * Invite discrète à installer le portail sur l'écran d'accueil (PWA).
 * Android/Chrome : bouton « Installer » (beforeinstallprompt). iOS Safari : consigne Partager → Sur l'écran d'accueil.
 * Masquée si déjà installée (display-mode standalone) ou après fermeture (30 jours).
 */
import { useEffect, useState } from "react";
import { Share, Smartphone, X } from "lucide-react";

const KEY = "portal_install_dismissed_at";
const THIRTY_DAYS = 30 * 24 * 3600 * 1000;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
const recentlyDismissed = () => {
  const t = Number(localStorage.getItem(KEY) || 0);
  return t && Date.now() - t < THIRTY_DAYS;
};

export default function InstallAppBanner({ primaryColor }) {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return undefined;
    if (isIOS()) setVisible(true);
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); setVisible(true); };
    const onInstalled = () => setVisible(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setVisible(false); };
  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setVisible(false);
    setPrompt(null);
  };

  if (!visible) return null;
  return (
    <div className="mb-4 rounded-2xl border bg-white p-3 sm:p-4 flex items-center gap-3 shadow-sm" data-testid="portal-install-banner">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white" style={{ backgroundColor: primaryColor }}>
        <Smartphone className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">Ajoutez le portail à votre écran d'accueil</p>
        {prompt ? (
          <p className="text-xs text-gray-500">Accès en un tap, comme une application.</p>
        ) : (
          <p className="text-xs text-gray-500 inline-flex items-center gap-1 flex-wrap">
            Touchez <Share className="w-3.5 h-3.5 inline" /> <strong>Partager</strong> puis « Sur l'écran d'accueil ».
          </p>
        )}
      </div>
      {prompt && (
        <button type="button" onClick={install} className="px-3 py-2 rounded-lg text-white text-xs font-semibold flex-shrink-0" style={{ backgroundColor: primaryColor }} data-testid="portal-install-btn">
          Installer
        </button>
      )}
      <button type="button" onClick={dismiss} className="p-2 text-gray-400 hover:text-gray-700 tap-target flex items-center justify-center" aria-label="Fermer" data-testid="portal-install-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
